import * as THREE from 'three';
import { HARMONY } from '../utils/constants.js';
import { events, EVT } from '../utils/events.js';
import { expandSong, parseCell, drumNoteForRow } from '../audio/songFormat.js';

/**
 * FlockingSystem - Boids flocking + 4/4 musical sequencer
 *
 * Each flock (species) is an instrument section that plays
 * a looping 4-beat bar pattern. As you walk across the
 * planet you hear different instrument groups fade in/out
 * based on proximity (spatial audio in AudioEngine).
 */

const _v = new THREE.Vector3();
const _sep = new THREE.Vector3();
const _ali = new THREE.Vector3();
const _coh = new THREE.Vector3();
const _wander = new THREE.Vector3();

export class FlockingSystem {
  constructor() {
    /** @type {Map<number, {creatures: any[], meshes: THREE.Group[], leader: any}>} */
    this.flocks = new Map();

    this.planetCenter = new THREE.Vector3();
    this.planetRadius = 100;
    this.heightResolver = null;
    this.tempo = 72;

    /** Per-flock bar sequencer state */
    this._barTimers = new Map();

    /** HarmonySystem reference */
    this.harmony = null;

    /**
     * Literal song performance (the reveal): when `songMode` is true and a
     * song is set, flocks with songNotes perform the previous player's EXACT
     * note grid on one shared clock instead of procedural patterns.
     */
    this.songMode = false;
    this._song = null;          // { stepMaps: Map<speciesId, Map<step, note[]>>, stepDur, loopSteps }
    this._songClock = 0;
    this._songNoteRotor = 0;
  }

  /**
   * Precompute per-species step→notes maps from the saved composition.
   * Mirrors MusicMaker's grid exactly: 32 sixteenth-note columns, 14 rows
   * mapped onto the song's scale (row 0 = highest pitch), base octave 3.
   * @param {object|null} songData  { tracks, bpm, key, scaleName } or null
   */
  setSong(songData, baseOctaveFallback = 3) {
    this._song = null;
    this._songClock = 0;
    const expanded = expandSong(songData, baseOctaveFallback);
    if (!expanded) return;

    this._song = {
      stepDur: expanded.stepDur,
      loopSteps: expanded.cols,
      buildStepMap: (rawCells, instrumentRole) => {
        const map = new Map();
        for (const cell of rawCells || []) {
          const p = parseCell(cell);
          if (!p) continue;
          // v2 percussion rows are drum lanes, not pitches
          const isDrums = instrumentRole === 'percussion' && typeof cell !== 'string';
          const note = isDrums ? drumNoteForRow(p.row) : expanded.scaleNotes[p.row];
          if (!note) continue;
          if (!map.has(p.col)) map.set(p.col, []);
          map.get(p.col).push({ note, durSteps: p.dur, vel: p.vel ?? 1 });
        }
        return map;
      },
      stepMaps: new Map(),
    };
  }

  /* ── Setup ────────────────────────────────────── */

  setPlanet(center, radius, heightResolver, tempo) {
    this.planetCenter.copy(center);
    this.planetRadius = radius;
    this.heightResolver = heightResolver;
    this.tempo = tempo || 72;
  }

  setHarmony(harmony) {
    this.harmony = harmony;
  }

  buildFlocks(creatures, meshes) {
    this.flocks.clear();
    this._barTimers.clear();

    creatures.forEach((creature, i) => {
      if (creature.collected) return;
      const sid = creature.speciesId ?? 0;
      if (!this.flocks.has(sid)) {
        this.flocks.set(sid, { creatures: [], meshes: [], leader: null });
      }
      const flock = this.flocks.get(sid);
      flock.creatures.push(creature);
      if (meshes[i]) flock.meshes.push(meshes[i]);
      if (creature.isLeader) flock.leader = creature;
    });

    // Use the harmony system's beatsPerBar (from time signature) if available
    const beatsPerBar = this.harmony?.beatsPerBar || HARMONY.DEFAULT_BEATS_PER_BAR;
    const beatDur = 60 / this.tempo;
    const barDuration = beatDur * beatsPerBar;

    for (const [sid, flock] of this.flocks) {
      if (!flock.leader && flock.creatures.length > 0) {
        flock.creatures[0].isLeader = true;
        flock.leader = flock.creatures[0];
      }

      // Genre-aware phrase length: breakcore changes more often, ambient less
      const genre = this.harmony?.genre || 'orchestral';
      const barsPerPhrase = genre === 'breakcore' ? 2
                          : genre === 'ambient' || genre === 'cinematic' ? 8
                          : genre === 'jazz' ? 4
                          : genre === 'minimal' ? 8
                          : 4;

      this._barTimers.set(sid, {
        elapsed: 0,
        barDuration,
        firedBeats: new Set(),
        currentPattern: null,
        barCount: 0,
        barsPerPhrase,
        phraseSeed: sid * 7919,
      });

      events.emit(EVT.FLOCK_FORMED, { speciesId: sid, count: flock.creatures.length });
    }
  }

  /* ── Per-frame update ─────────────────────────── */

  update(dt, time) {
    const performing = this.songMode && this._song;
    for (const [speciesId, flock] of this.flocks) {
      if (flock.meshes.length === 0) continue;

      // Update animations
      for (const mesh of flock.meshes) {
        if (mesh.userData.mixer) {
          mesh.userData.mixer.update(dt);
        }
      }

      this._updateFlockMovement(flock, dt, time);
      // Species holding a song track go silent here; the shared song clock
      // below performs them in sync. Others keep their procedural calls.
      if (!(performing && flock.leader?.songNotes?.length)) {
        this._updateBarSequencer(speciesId, flock, dt, time);
      }
    }

    if (performing) this._updateSongPerformance(dt, time);
  }

  /* ── Literal song performance (the reveal) ────── */

  /**
   * All song-holding flocks perform the saved note grid on ONE clock, so the
   * planet plays the previous player's actual composition — not a pattern
   * inspired by it. Notes rotate across flock members for spatial spread.
   */
  _updateSongPerformance(dt, time) {
    const song = this._song;
    const prevStep = Math.floor(this._songClock / song.stepDur);
    this._songClock += dt;
    const loopDur = song.stepDur * song.loopSteps;
    if (this._songClock >= loopDur) this._songClock -= loopDur;
    const currStep = Math.floor(this._songClock / song.stepDur);
    if (currStep === prevStep) return;

    // Steps crossed this frame (usually one; handles wrap + frame hitches)
    const crossed = [];
    let s = (prevStep + 1) % song.loopSteps;
    for (let guard = 0; guard < song.loopSteps; guard++) {
      crossed.push(s);
      if (s === currStep) break;
      s = (s + 1) % song.loopSteps;
    }

    for (const [speciesId, flock] of this.flocks) {
      const leader = flock.leader;
      if (!leader?.songNotes?.length) continue;

      let stepMap = song.stepMaps.get(speciesId);
      if (!stepMap) {
        stepMap = song.buildStepMap(leader.songNotes, leader.voice?.instrumentRole);
        song.stepMaps.set(speciesId, stepMap);
      }

      for (const step of crossed) {
        const notes = stepMap.get(step);
        if (!notes) continue;
        for (const ev of notes) {
          const creature = flock.creatures[this._songNoteRotor++ % flock.creatures.length];
          if (!creature || creature.collected) continue;
          events.emit(EVT.CREATURE_CALL, {
            speciesId,
            creature,
            note: ev.note,
            // Honor the drawn note length (95% to leave a gap before retrigger)
            duration: ev.durSteps * song.stepDur * 0.95,
            velocity: ev.vel ?? 1,
            beat: step / 4,
            time,
          });
        }
      }
    }
  }

  /* ── 4/4 Bar Sequencer ───────────────────────── */

  _updateBarSequencer(speciesId, flock, dt, time) {
    const timer = this._barTimers.get(speciesId);
    if (!timer || !this.harmony) return;

    timer.elapsed += dt;

    // New bar — clear fired events, advance bar counter
    if (timer.elapsed >= timer.barDuration) {
      timer.elapsed -= timer.barDuration;
      timer.firedBeats.clear();
      timer.barCount++;

      // Only regenerate pattern when starting a new phrase
      // Within a phrase, the same pattern repeats
      if (timer.barCount % timer.barsPerPhrase === 0) {
        timer.currentPattern = null;
      }
    }

    const beatDur = 60 / this.tempo;
    const currentBeat = timer.elapsed / beatDur;

    // Generate pattern lazily — stable seed so same phrase repeats identically
    if (!timer.currentPattern) {
      const leader = flock.leader;
      if (!leader) return;
      const voice = leader.voice;
      const roleId = voice.instrumentRole || 'flute';
      // Seed = species base + phrase number (changes every barsPerPhrase bars)
      const phraseNum = Math.floor(timer.barCount / timer.barsPerPhrase);
      const seed = timer.phraseSeed + phraseNum * 131;
      timer.currentPattern = this.harmony.generateBarPattern(
        roleId,
        voice.scaleDegree ?? 0,
        voice.octaveOffset ?? 0,
        seed,
      );
    }

    // Fire events for beats that have been reached
    const pattern = timer.currentPattern;
    for (let i = 0; i < pattern.length; i++) {
      const evt = pattern[i];
      if (timer.firedBeats.has(i)) continue;
      if (currentBeat >= evt.beat) {
        timer.firedBeats.add(i);

        // Spread notes across flock members
        const creatureIdx = i % flock.creatures.length;
        const creature = flock.creatures[creatureIdx];
        if (!creature || creature.collected) continue;

        events.emit(EVT.CREATURE_CALL, {
          speciesId,
          creature,
          note: evt.note,
          duration: evt.duration,
          beat: evt.beat,
          time,
        });
      }
    }
  }

  /* ── Boids flocking ──────────────────────────── */

  _updateFlockMovement(flock, dt, time) {
    const {
      FLOCK_SEPARATION: sepDist,
      FLOCK_ALIGNMENT: aliW,
      FLOCK_COHESION: cohW,
      FLOCK_SEPARATION_WEIGHT: sepW,
      FLOCK_MAX_SPEED: maxSpeed,
      FLOCK_WANDER: wanderW,
    } = HARMONY;

    const meshes = flock.meshes;
    const creatures = flock.creatures;

    for (let i = 0; i < meshes.length; i++) {
      const mesh = meshes[i];
      const data = creatures[i];
      if (!mesh || !data) continue;

      _sep.set(0, 0, 0);
      _ali.set(0, 0, 0);
      _coh.set(0, 0, 0);
      let sepCount = 0;
      let neighborCount = 0;

      const pos = mesh.position;

      for (let j = 0; j < meshes.length; j++) {
        if (i === j) continue;
        const other = meshes[j];
        if (!other) continue;

        _v.copy(other.position).sub(pos);
        const dist = _v.length();

        if (dist < HARMONY.FLOCK_RADIUS && dist > 0.01) {
          neighborCount++;
          _coh.add(other.position);

          const oData = creatures[j];
          if (oData?.velocity) {
            _ali.add(_v.set(oData.velocity.x, oData.velocity.y, oData.velocity.z));
          }

          if (dist < sepDist) {
            const force = (sepDist - dist) / sepDist;
            _sep.sub(_v.copy(other.position).sub(pos).normalize().multiplyScalar(force));
            sepCount++;
          }
        }
      }

      const accel = new THREE.Vector3();

      if (neighborCount > 0) {
        _coh.divideScalar(neighborCount).sub(pos);
        if (_coh.lengthSq() > 0.001) _coh.normalize().multiplyScalar(cohW);
        accel.add(_coh);

        _ali.divideScalar(neighborCount);
        if (_ali.lengthSq() > 0.001) _ali.normalize().multiplyScalar(aliW);
        accel.add(_ali);
      }

      if (sepCount > 0) {
        _sep.divideScalar(sepCount);
        if (_sep.lengthSq() > 0.001) _sep.normalize().multiplyScalar(sepW);
        accel.add(_sep);
      }

      _wander.set(
        Math.sin(time * 0.7 + i * 2.3) * wanderW,
        Math.sin(time * 0.5 + i * 1.7) * wanderW * 0.3,
        Math.cos(time * 0.6 + i * 3.1) * wanderW,
      );
      accel.add(_wander);

      const radial = pos.clone().sub(this.planetCenter);
      const dist = radial.length();
      if (dist > 0.01) {
        const normal = radial.clone().divideScalar(dist);
        accel.addScaledVector(normal, -accel.dot(normal));
      }

      const vel = data.velocity;
      const speed = data.behavior?.speed ?? 2;
      vel.x += accel.x * dt * speed;
      vel.y += accel.y * dt * speed;
      vel.z += accel.z * dt * speed;

      _v.set(vel.x, vel.y, vel.z);
      const currentSpeed = _v.length();
      if (currentSpeed > maxSpeed) {
        const scale = maxSpeed / currentSpeed;
        vel.x *= scale;
        vel.y *= scale;
        vel.z *= scale;
      }

      const damping = 0.95;
      vel.x *= damping;
      vel.y *= damping;
      vel.z *= damping;

      mesh.position.x += vel.x * dt;
      mesh.position.y += vel.y * dt;
      mesh.position.z += vel.z * dt;

      this._snapToSurface(mesh, data);

      const surfaceNormal = mesh.position.clone().sub(this.planetCenter).normalize();
      mesh.up.copy(surfaceNormal);
      if (currentSpeed > 0.1) {
        const lookTarget = mesh.position.clone().add(_v.set(vel.x, vel.y, vel.z).normalize());
        mesh.lookAt(lookTarget);
        
        // Switch to "walk" animation if moving
        this._switchAnimation(mesh, 'walk');
      } else {
        // Switch to "idle" animation if stationary
        this._switchAnimation(mesh, 'idle');
      }
    }
  }

  /**
   * Cross-fade to a specific animation by name.
   */
  _switchAnimation(mesh, animName) {
    const { mixer, animations, currentAnim } = mesh.userData;
    if (!mixer || !animations || currentAnim === animName) return;

    // Look for a clip that matches the name (case-insensitive)
    const clip = animations.find(a => a.name.toLowerCase().includes(animName.toLowerCase()));
    if (!clip) return;

    const newAction = mixer.clipAction(clip);
    
    if (currentAnim) {
      const oldClip = animations.find(a => a.name.toLowerCase().includes(currentAnim.toLowerCase()));
      if (oldClip) {
        const oldAction = mixer.clipAction(oldClip);
        oldAction.fadeOut(0.3);
      }
    }

    newAction.reset().fadeIn(0.3).play();
    mesh.userData.currentAnim = animName;
  }

  _snapToSurface(mesh, data) {
    const dir = mesh.position.clone().sub(this.planetCenter);
    const dist = dir.length();
    if (dist < 0.01) return;
    dir.divideScalar(dist);

    let surfaceR = this.planetRadius;
    if (typeof this.heightResolver === 'function') {
      surfaceR = this.heightResolver(dir);
    }

    mesh.position.copy(this.planetCenter).addScaledVector(dir, surfaceR + (data.size || 0.5) * 0.3);
  }

  /* ── Cleanup ──────────────────────────────────── */

  removeCreature(creatureId) {
    for (const [sid, flock] of this.flocks) {
      const idx = flock.creatures.findIndex(c => c.id === creatureId);
      if (idx >= 0) {
        flock.creatures.splice(idx, 1);
        flock.meshes.splice(idx, 1);
        if (flock.leader?.id === creatureId) {
          flock.leader = flock.creatures[0] || null;
          if (flock.leader) flock.leader.isLeader = true;
        }
        if (flock.creatures.length === 0) {
          this.flocks.delete(sid);
          this._barTimers.delete(sid);
        }
        return;
      }
    }
  }

  clear() {
    this.flocks.clear();
    this._barTimers.clear();
  }
}
